import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Premium reusable slider component with touch/mouse support
 * Features: Auto-slide, loop mode, pagination, RTL/LTR support
 */
interface SliderProps {
  children: React.ReactNode;
  autoSlideInterval?: number;
  showPagination?: boolean;
  loop?: boolean;
  className?: string;
  slideClassName?: string;
  onSlideChange: (index: number) => void;
}

export default function Slider({
  children,
  autoSlideInterval = 5000,
  showPagination = true,
  loop = true,
  className = "",
  slideClassName = "",
  onSlideChange,
}: SliderProps) {
  const slides = React.Children.toArray(children);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);

  // RTL detection
  const isRTL =
    typeof document !== "undefined" && document.documentElement.dir === "rtl";

  const totalSlides = slides.length;

  // Navigation functions
  const goToSlide = useCallback(
    (index: number, directionHint?: number) => {
      let newIndex = index;
      if (loop) {
        if (index < 0) newIndex = totalSlides - 1;
        if (index >= totalSlides) newIndex = 0;
      } else {
        newIndex = Math.max(0, Math.min(index, totalSlides - 1));
      }

      const computedDirection =
        directionHint ?? (newIndex > currentIndex ? 1 : -1);
      setDirection(computedDirection);
      setCurrentIndex(newIndex);
      onSlideChange?.(newIndex);
    },
    [currentIndex, totalSlides, loop, onSlideChange]
  );

  const goNext = useCallback(() => {
    goToSlide(currentIndex + 1, 1);
  }, [currentIndex, goToSlide]);

  const goPrev = useCallback(() => {
    goToSlide(currentIndex - 1, -1);
  }, [currentIndex, goToSlide]);

  // Auto-slide effect
  useEffect(() => {
    if (!autoSlideInterval || isPaused || isDragging) return;

    const interval = setInterval(goNext, autoSlideInterval);
    return () => clearInterval(interval);
  }, [autoSlideInterval, isPaused, isDragging, goNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goPrev();
      } else if (e.key === "ArrowRight") {
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev, isRTL]);

  // Drag handling
  const handleDragEnd = (_: any, info: any) => {
    setIsDragging(false);
    const threshold = 50;
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    if (Math.abs(offset) > threshold || Math.abs(velocity) > 500) {
      offset < 0 ? goNext() : goPrev();
    }
  };

  // Animation variants
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "20%" : "-20%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 200, damping: 25 },
        opacity: { duration: 0.5 },
      },
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "20%" : "-20%",
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 200, damping: 25 },
        opacity: { duration: 0.4 },
      },
    }),
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides Container */}
      <div className="relative w-full">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            className={`w-full cursor-grab active:cursor-grabbing ${slideClassName}`}
          >
            {slides[currentIndex]}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Arrows */}
      <NavigationArrow
        direction="prev"
        onClick={goPrev}
        isRTL={isRTL}
        disabled={!loop && currentIndex === 0}
      />
      <NavigationArrow
        direction="next"
        onClick={goNext}
        isRTL={isRTL}
        disabled={!loop && currentIndex === totalSlides - 1}
      />

      {/* Pagination Dots - Hidden on mobile */}
      {showPagination && totalSlides > 1 && (
        <div className="hidden md:flex justify-center gap-3 mt-8">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index, index > currentIndex ? 1 : -1)}
              className={`relative rounded-full transition-all duration-500 ${
                index === currentIndex
                  ? "w-6 h-1.5 md:w-8 md:h-2 bg-primary shadow-[0_0_6px_rgba(227,30,36,0.6)]"
                  : "w-1.5 h-1.5 md:w-2 md:h-2 bg-white/20 hover:bg-white/30"
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

    </div>
  );
}

// Navigation Arrow Component
function NavigationArrow({ direction, onClick, isRTL, disabled }: { direction: 'next' | 'prev', onClick: () => void, isRTL: boolean, disabled: boolean }) {
  const isNext = direction === "next";
  const shouldFlip = isRTL ? !isNext : isNext;

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`
        hidden md:flex md:items-center md:justify-center
        absolute top-1/2 -translate-y-1/2 z-30
        w-14 h-14
        bg-black/20 hover:bg-primary backdrop-blur-md
        rounded-2xl border border-white/10
        text-white
        transition-all duration-500
        hover:shadow-[0_0_30px_rgba(227,30,36,0.3)]
        disabled:opacity-10 disabled:cursor-not-allowed
        ${isNext ? "right-6" : "left-6"}
      `}
      whileHover={{ scale: disabled ? 1 : 1.1 }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      aria-label={isNext ? "Next slide" : "Previous slide"}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`w-5 h-5 md:w-6 md:h-6 ${shouldFlip ? "" : "rotate-180"}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </motion.button>
  );
}
